import { ref, uploadBytes } from 'firebase/storage';

import { storage } from './firebase.js';
import { logger } from './logger.js';

/**
 * Upload a file to Firebase Storage and return the object path.
 *
 * Path returned (not a download URL) so the caller can hand it to a Cloud
 * Function that reads via admin SDK — we never round-trip through HTTPS.
 */
export async function uploadFile(path: string, file: File): Promise<string> {
  const r = ref(storage, path);
  await uploadBytes(r, file, {
    contentType: file.type || 'application/octet-stream',
    cacheControl: 'private, max-age=0',
  });
  logger.info('storage upload ok', { path, size: file.size, type: file.type });
  return path;
}

/**
 * Build the canonical source-logo path for a tenant.
 * One file per upload so a failed regenerate doesn't leave us in a half-state —
 * the previous source stays put until the new one is written + the function
 * succeeds + branding.logoUrl is bumped.
 */
export function logoSourcePath(tenantId: string, file: File): string {
  const ts = Date.now();
  const ext = extFor(file);
  return `tenants/${tenantId}/uploads/logo-${ts}.${ext}`;
}

function extFor(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{2,5}$/.test(fromName)) return fromName;
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/jpeg') return 'jpg';
  if (file.type === 'image/svg+xml') return 'svg';
  if (file.type === 'image/webp') return 'webp';
  return 'bin';
}
